import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { updateUser } from '../store/slices/authSlice';
import {
  USERNAME_MAX, USERNAME_HINT, sanitiseUsernameInput, validateUsernameFormat,
} from '../utils/usernameRules';

const OnboardingUsername = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);

  const [username, setUsername] = useState(user?.username || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // App Store guideline 1.2 — OAuth sign-ups don't pass through the
  // register screen's checkbox, so the username step is the canonical EULA
  // acceptance gate for new accounts.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const onChange = (e) => {
    setUsername(sanitiseUsernameInput(e.target.value));
    if (error) setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    // Shared with the rename field and mirrored from the server, so the two
    // screens can't disagree about what a valid handle is.
    const formatError = validateUsernameFormat(username);
    if (formatError) {
      setError(formatError);
      return;
    }
    if (!acceptedTerms) {
      setError('You must agree to the Terms of Use to continue.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/auth/username', { username, acceptedTerms: true });
      if (data.token) localStorage.setItem('token', data.token);
      dispatch(updateUser(data.user));
      toast.success('Welcome!');
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save username');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="auth-bg-fixed" style={{ backgroundImage: "url('/images/Background.png')" }} />
      <div className="onboarding-wrap">
        <div className="auth-glass-card onboarding-card">
          <div className="auth-form-header">
            <h2 className="auth-form-title">Pick a username</h2>
            <p className="auth-form-sub">This is how others will find you.</p>
          </div>
          <form onSubmit={submit} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">Username</label>
              <input
                className="auth-input"
                type="text"
                value={username}
                onChange={onChange}
                placeholder="yourname"
                autoFocus
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={USERNAME_MAX}
              />
              <div className="onboarding-hint">{USERNAME_HINT}</div>
            </div>
            <label className="auth-terms-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ color: '#c8cfe0', fontSize: '0.85rem', lineHeight: 1.5 }}>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline' }}>
                  Terms of Use
                </Link>{' '}
                and confirm I will not post objectionable content or abuse other users.
              </span>
            </label>
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={saving || !username || !acceptedTerms}>
              {saving ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default OnboardingUsername;
